import * as React from 'react';
import { translate } from 'components/translate-hoc';
import { ConfigContext } from 'modules/context';
import { ITranslationProps } from 'types/translation';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';

import ResubmitCodeButton from '../../resubmit-code-button';
import { IRedeemContent } from '../redemption.reducer';
import {
    IContentToDisplay,
    IRedemptionSuccessProps,
    IRedemptionSuccessState
} from './redemption-success.types';
import {
    buildContentItemTranslationKey,
    getContentToDisplay
} from './redemtion-success.utils';
import { getTrackingFlowSubType } from 'utils/analytics';

import { TEXT_KEYS } from '../redemption.constants';
import {
    INITIAL_SUCCESS_ANALYTICS_DATA,
    ON_SUBMIT_ANOTHER_CODE_ANALYTICS_DATA
} from './redemption-success.constants';

import './redemption-success.styles.less';

export class RedemptionSuccess extends React.Component<
IRedemptionSuccessProps & ITranslationProps,
IRedemptionSuccessState
> {
    public state: IRedemptionSuccessState = {
        contentToDisplay: getContentToDisplay(this.props)
    };

    public componentDidMount() {
        const { config: { widgetName } } = this.context;
        return AnalyticsService.sendFlowEvent({
            ...INITIAL_SUCCESS_ANALYTICS_DATA,
            flowSubType: getTrackingFlowSubType(widgetName),
            platformSelection: this.props.platform
        });
    }

    public render(): JSX.Element {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const { getModeClass } = this.context;
        const length = Object.keys(this.state.contentToDisplay).length;

        return (
            <div className={getModeClass('sitegen-redeem-success')}>
                <div className="sitegen-redeem-success__title">
                    {translateKey(TEXT_KEYS.UNLOCK_YOUR_CONTENT)}
                </div>
                {length > 1
                    ? this.renderMultipleItemsContent()
                    : this.renderSingleItemContent()
                }
            </div>
        );
    }

    public renderSingleItemContent(): JSX.Element | null {
        const keys: string[] = Object.keys(this.state.contentToDisplay);
        const packId: string = keys[0];

        /* Usually API should return at least one item, but we'll check this anyways */
        if (!packId) {
            return null;
        }

        const { config: { widgetName } } = this.context;
        const {
            dangerouslyTranslateWithHTML: translateKey,
            originalTranslateKey
        } = this.props;

        /* Usually i18next fallbacks to string representation of key, changing it to empty string */
        const item: string = originalTranslateKey([`${TEXT_KEYS.REDEEM_SUCCESS_GIFT_DESCRIPTION}__${packId}`, '']);
        const congratulations: string = item && item.length
            ? translateKey(TEXT_KEYS.REDEEM_SUCCESS_CONGRATS_MESSAGE_SINGLE_ITEM, { item })
            : '';

        const description: string = this.getContentItemTranslation(this.state.contentToDisplay[packId].responseItem);
        const src: string = this.state.contentToDisplay[packId].contentPackItem.imageSrc as string;
        const singleItemImage: JSX.Element | null = src
            ? <img
                className="sitegen-redeem-success__single-item-image"
                onError={this.onImageError}
                src={src}
                alt={item}
            />
            : null;

        return (
            <div className="sitegen-redeem-success__single-item-content">
                <div className="sitegen-redeem-success__content-title">{translateKey(TEXT_KEYS.REDEEM_SUCCESS_TITLE)}</div>
                <div className="sitegen-redeem-success__congratulation-message">{congratulations}</div>
                <div className="sitegen-redeem-success__explanation">{description}</div>
                {singleItemImage}
                <div className="sitegen-redeem-success__button-container">
                    <ResubmitCodeButton
                        additionalAnalyticsInfo={{
                            ...ON_SUBMIT_ANOTHER_CODE_ANALYTICS_DATA,
                            flowSubType: getTrackingFlowSubType(widgetName)
                        }}
                    />
                </div>
            </div>
        );
    }

    public renderMultipleItemsContent(): JSX.Element {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const { config: { widgetName } } = this.context;

        return (
            <div className="sitegen-redeem-success__multiple-items-content">
                <div className="sitegen-redeem-success__content-title">{translateKey(TEXT_KEYS.REDEEM_SUCCESS_TITLE)}</div>
                <div className="sitegen-redeem-success__congratulation-message">
                    {translateKey(TEXT_KEYS.REDEEM_SUCCESS_CONGRATS_MESSAGE_MULTIPLE_ITEMS)}
                </div>

                {this.renderUnlockedContentItems()}
                <ResubmitCodeButton
                    additionalAnalyticsInfo={{
                        ...ON_SUBMIT_ANOTHER_CODE_ANALYTICS_DATA,
                        flowSubType: getTrackingFlowSubType(widgetName)
                    }}
                />
            </div>
        );
    }

    /** TODO: Not used right now because of translation issues: GEN-1923 */
    public renderMainDescriptionOrItemName(): string | null {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const keys: string[] = Object.keys(this.state.contentToDisplay);

        if (keys.length > 1) {
            return translateKey(TEXT_KEYS.REDEEM_SUCCESS_MULTIPLE_REWARDS);
        }

        if (keys.length === 1) {
            const packId: string | number = this.state.contentToDisplay[keys[0]].contentPackItem.packId;
            return translateKey(`${TEXT_KEYS.REDEEM_SUCCESS_GIFT_DESCRIPTION}__${packId}`);
        }

        return null;
    }

    public renderUnlockedContentItems(): JSX.Element[] {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const contentToDisplay: IContentToDisplay = getContentToDisplay(this.props);
        const keys: string[] = Object.keys(contentToDisplay);

        return keys.map((packId: string): JSX.Element => {
            const name: string = translateKey(`${TEXT_KEYS.REDEEM_SUCCESS_GIFT_DESCRIPTION}__${packId}`);
            const description: string = this.getContentItemTranslation(contentToDisplay[packId].responseItem);

            const imageSrc: string = contentToDisplay[packId].contentPackItem.imageSrc as string;
            const image: JSX.Element | null = (imageSrc)
                ? <img className="sitegen-redeem-gift__image" src={imageSrc} alt=""/>
                : null;

            return (
                <div key={packId} className={`sitegen-redeem-gift ${packId}`}>
                    {image}

                    <div className="sitegen-redeem-gift__description">
                        <div className="sitegen-redeem-gift__name">{name}</div>
                        <div className="sitegen-redeem-gift__action">{description}</div>
                    </div>
                </div>
            );
        });
    }

    public getContentItemTranslation(contentItem: IRedeemContent): string {
        const {
            dangerouslyTranslateWithHTML: translateKey,
            platform
        } = this.props;
        const { config: { widgetName } } = this.context;
        const { redeemActionDetails: { key } } = contentItem;

        const textKey: string = buildContentItemTranslationKey(
            contentItem,
            platform,
            widgetName
        );
        return textKey ? translateKey(textKey, { key }) : '';
    }

    private onImageError(e: React.SyntheticEvent<HTMLImageElement>): void {
        const img: HTMLImageElement = (e.target as HTMLImageElement);
        img.onerror = () => {};
        img.style.visibility = 'hidden';
    }
}

RedemptionSuccess.contextType = ConfigContext;

export default translate()(RedemptionSuccess);
